import React from "react";
import OrangeScoreBadge from "@/hq/OrangeScoreBadge";

/**
 * OrangeFixProofCard — the ONE Before/After proof visual across HQ,
 * Client Profile, and the customer /fix/{token} page.
 *
 * Contract:
 *   proof.available === true (parent renders nothing otherwise)
 *   proof.issue.title / .category
 *   proof.before  = { scan_date, orange_score, score_version, category_score }
 *   proof.after   = { scan_date, orange_score, score_version, category_score }
 *   proof.score_delta   (number | null when methodology_mismatch or missing)
 *   proof.category_delta (number | null)
 *   proof.methodology_mismatch (bool)
 *   proof.verified_at (ISO)
 *   proof.issue_has_since_returned (bool — historical proof stays intact)
 *
 * Presentation rules (spec):
 *   • Primary claim = ISSUE RESOLVED ✓. Score comparison is secondary.
 *   • Negative or zero delta shown honestly — never hidden, never inverted.
 *   • V1 vs V2 → methodology notice, no simplistic +X delta.
 *   • Reuses canonical OrangeScoreBadge fruit component.
 *   • Respects prefers-reduced-motion via CSS media query on the reveal.
 */
const CATEGORY_LABEL = {
  performance: "Performance",
  mobile:      "Mobile experience",
  assets:      "Assets & media",
  technical:   "Technical health",
  security:    "Security health",
  efficiency:  "Efficiency",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined,
                              { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

function DeltaBadge({ delta }) {
  if (delta == null) {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase bg-[#FFF9E6] text-[#8A6D00] border border-[#F5E4B0]"
            data-testid="fix-proof-delta-mismatch">
        Method changed
      </span>
    );
  }
  if (delta === 0) {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase bg-[#F6F5F2] text-[#4A4A4A] border border-[#EAEAEA]"
            data-testid="fix-proof-delta">
        No overall score change
      </span>
    );
  }
  const positive = delta > 0;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase ${
      positive
        ? "bg-[#EBFBF2] text-[#0FA958] border border-[#B7EBCF]"
        : "bg-[#FFF7EE] text-[#C1350B] border border-[#FCE1C7]"
    }`} data-testid="fix-proof-delta">
      {positive ? "+" : ""}{delta} points
    </span>
  );
}

export default function OrangeFixProofCard({ proof, variant = "default",
                                              customer = false }) {
  if (!proof || !proof.available && proof.available !== undefined) {
    // Server-side proof-not-available shape — render nothing.
    return null;
  }
  // Some callers pass the trimmed customer view (no .available flag) —
  // treat presence of before/after as "renderable".
  if (!proof.before || !proof.after) return null;

  const beforeScore = proof.before.orange_score;
  const afterScore  = proof.after.orange_score;
  const catKey      = (proof.issue?.category || "").toLowerCase();
  const catLabel    = CATEGORY_LABEL[catKey] || proof.issue?.category || null;
  const catBefore   = proof.before.category_score;
  const catAfter    = proof.after.category_score;
  const showCategory =
    catLabel && catBefore != null && catAfter != null && !proof.methodology_mismatch;

  return (
    <div className="rounded-3xl border-2 border-[#0A0A0A] bg-white p-6 md:p-8 orange-fix-proof-card"
         data-testid="orange-fix-proof-card">
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .orange-fix-proof-card { animation: ofp-fade-in 400ms ease-out both; }
          .orange-fix-proof-card .ofp-arrow { animation: ofp-slide 500ms ease-out both; }
        }
        @keyframes ofp-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ofp-slide {
          from { opacity: 0; transform: translateX(-6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Eyebrow */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase font-black text-[#FF6A00]">
            Orange Fix · Verified result
          </div>
          <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-[#EBFBF2] border-2 border-[#0FA958] text-[#0FA958] font-black text-[11px] tracking-[0.22em] uppercase"
               data-testid="fix-proof-issue-resolved">
            Issue resolved ✓
          </div>
        </div>
        {proof.issue_has_since_returned && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase bg-[#FFF7EE] text-[#C1350B] border border-[#FCE1C7]"
                data-testid="fix-proof-returned-notice">
            This issue has since returned
          </span>
        )}
      </div>

      {/* Issue */}
      <h3 className="mt-5 font-black text-2xl md:text-3xl tracking-[-0.02em] text-[#0A0A0A]"
          data-testid="fix-proof-issue-title">
        {proof.issue?.title || "—"}
      </h3>
      {catLabel && (
        <div className="mt-1 text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">
          {catLabel}
        </div>
      )}

      {/* Before / After */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-6">
        <div className="flex flex-col items-center text-center" data-testid="fix-proof-before">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">Before fix</div>
          <div className="mt-3">
            <OrangeScoreBadge score={beforeScore} size="lg"
                              testId="fix-proof-before-badge"/>
          </div>
          <div className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A] font-bold">
            Orange Score{proof.before.score_version ? ` · ${proof.before.score_version.toUpperCase()}` : ""}
          </div>
          <div className="mt-1 text-[11px] text-[#8A8A8A]">{fmtDate(proof.before.scan_date)}</div>
        </div>

        <div className="hidden md:flex flex-col items-center ofp-arrow">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#FF6A00] font-black">Orange Fix</div>
          <div className="mt-1 text-4xl font-black text-[#FF6A00]">→</div>
        </div>
        <div className="md:hidden flex flex-col items-center ofp-arrow">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#FF6A00] font-black">Orange Fix</div>
          <div className="mt-1 text-3xl font-black text-[#FF6A00]">↓</div>
        </div>

        <div className="flex flex-col items-center text-center" data-testid="fix-proof-after">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">After fix</div>
          <div className="mt-3">
            <OrangeScoreBadge score={afterScore} size="lg"
                              testId="fix-proof-after-badge"/>
          </div>
          <div className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A] font-bold">
            Orange Score{proof.after.score_version ? ` · ${proof.after.score_version.toUpperCase()}` : ""}
          </div>
          <div className="mt-1 text-[11px] text-[#8A8A8A]">{fmtDate(proof.after.scan_date)}</div>
        </div>
      </div>

      {/* Delta (secondary evidence) */}
      <div className="mt-6 flex justify-center">
        <DeltaBadge delta={proof.methodology_mismatch ? null : proof.score_delta}/>
      </div>

      {/* Methodology notice */}
      {proof.methodology_mismatch && (
        <p className="mt-4 text-center text-[13px] text-[#4A4A4A] max-w-lg mx-auto"
           data-testid="fix-proof-methodology-notice">
          The Orange Score methodology changed between these scans, so the
          scores should not be compared directly. The issue-resolution proof
          above still comes from independent Orange Optimise diagnostics.
        </p>
      )}

      {/* Category context */}
      {showCategory && (
        <div className="mt-8 p-4 rounded-2xl border border-[#EAEAEA] bg-[#FFFDF6]"
             data-testid="fix-proof-category">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#FF6A00]">
            {catLabel}
          </div>
          <div className="mt-2 flex items-center gap-4 flex-wrap">
            <span className="text-[13px] text-[#4A4A4A]">
              Before <strong data-testid="fix-proof-cat-before" className="font-black text-[#0A0A0A]">{catBefore}</strong>
            </span>
            <span className="text-[13px] text-[#4A4A4A]">
              After <strong data-testid="fix-proof-cat-after" className="font-black text-[#0A0A0A]">{catAfter}</strong>
            </span>
            <DeltaBadge delta={proof.category_delta}/>
          </div>
        </div>
      )}

      {/* Negative-delta context — never hide, add supporting language. */}
      {!proof.methodology_mismatch && typeof proof.score_delta === "number"
        && proof.score_delta < 0 && (
        <p className="mt-4 text-center text-[12px] text-[#4A4A4A] max-w-lg mx-auto"
           data-testid="fix-proof-negative-note">
          The selected issue was successfully resolved. Other website health
          changes affected the overall Orange Score.
        </p>
      )}

      {/* Verified-by footer */}
      <div className="mt-8 pt-5 border-t border-[#EAEAEA] flex flex-col items-center text-center gap-1">
        <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#0FA958]">
          Verified by Orange Optimise
        </div>
        <div className="text-[13px] text-[#4A4A4A]" data-testid="fix-proof-verified-at">
          {fmtDate(proof.verified_at)}
        </div>
        <p className="mt-1 text-[13px] text-[#0A0A0A] max-w-md">
          Orange Optimise rescanned this website and confirmed that the
          selected issue is no longer detected.
        </p>
      </div>
    </div>
  );
}
